import { useEffect, useMemo, useState } from "react";
import { api, fmtDate } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ROLES, roleLabel } from "@/lib/constants";
import { toast } from "sonner";
import { Plus, Copy, KeyReturn, UserCircle, CaretUp, CaretDown, Columns, ArrowsDownUp } from "@phosphor-icons/react";
import { API_BASE, fileUrl } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";

// Columns config (key matches employee field used for sort/filter)
const ALL_COLUMNS = [
  { key: "employee_id", label: "Staff Code" },
  { key: "full_name", label: "Employee" },
  { key: "mobile", label: "Mobile" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role" },
  { key: "department_id", label: "Department" },
  { key: "designation_id", label: "Designation" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Created" },
];

export default function Employees() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [activationLink, setActivationLink] = useState("");
  const empty = {
    full_name: "", mobile: "", email: "", employee_id: "",
    department_id: "", designation_id: "", reporting_manager_id: "",
    role: "employee", is_department_manager: false, status: "active",
    set_password_directly: false, password: "", profile_picture_file_id: null,
  };
  const [form, setForm] = useState(empty);
  const [picUploading, setPicUploading] = useState(false);

  // Excel-style state
  const [filters, setFilters] = useState({}); // {key: text}
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [visibleCols, setVisibleCols] = useState(() => Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, true])));
  const [colOrder, setColOrder] = useState(ALL_COLUMNS.map((c) => c.key));

  const uploadPic = async (file) => {
    if (!file) return;
    setPicUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("category", "profile");
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, profile_picture_file_id: r.data.id }));
      toast.success("Profile picture uploaded");
    } catch { toast.error("Upload failed"); } finally { setPicUploading(false); }
  };
  const canManage = hasRole(user, "super_admin", "hr");

  const load = async () => {
    const r = await api.get("/employees");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("/departments").then((r) => setDepartments(r.data));
    api.get("/designations").then((r) => setDesignations(r.data));
  }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setActivationLink(""); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setActivationLink(""); setOpen(true); };
  const save = async () => {
    if (!form.full_name || !form.mobile) return toast.error("Name and mobile required");
    if (!editing && form.set_password_directly && (!form.password || form.password.length < 6)) {
      return toast.error("Password must be at least 6 characters");
    }
    try {
      if (editing) {
        const { set_password_directly, password, ...rest } = form;
        await api.put(`/employees/${editing.id}`, rest);
        toast.success("Employee updated");
      } else {
        const payload = { ...form };
        if (!form.set_password_directly) delete payload.password;
        delete payload.set_password_directly;
        if (!payload.employee_id?.trim()) delete payload.employee_id;
        const r = await api.post("/employees", payload);
        if (r.data.activation_link) {
          setActivationLink(r.data.activation_link);
          toast.success("Employee created. Share activation link.");
        } else {
          toast.success("Employee created. Password is set — they can log in now.");
        }
      }
      load();
      if (editing) setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Disable ${it.full_name}?`)) return;
    await api.delete(`/employees/${it.id}`); toast.success("Disabled"); load();
  };
  const resetPwd = async (it) => {
    const choice = window.prompt(`Reset password for ${it.full_name}.\n\nLeave blank to send an activation link, OR type a new password (min 6 chars) to set it directly:`, "");
    if (choice === null) return;
    const trimmed = choice.trim();
    const body = trimmed ? { password: trimmed } : {};
    try {
      const r = await api.post(`/employees/${it.id}/reset-password`, body);
      if (r.data.activation_link) {
        setActivationLink(r.data.activation_link); setEditing(it);
        setForm({ ...empty, ...it }); setOpen(true);
        toast.success("Activation link generated");
      } else { toast.success("Password set successfully"); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const fullLink = (path) => `${window.location.origin}${path}`;

  const deptName = (id) => departments.find((d) => d.id === id)?.name || "";
  const desigName = (id) => designations.find((d) => d.id === id)?.name || "";

  // Derived list
  const cellValue = (it, key) => {
    if (key === "department_id") return deptName(it.department_id);
    if (key === "designation_id") return desigName(it.designation_id);
    if (key === "created_at") return it.created_at || "";
    return it[key] || "";
  };

  const filtered = useMemo(() => {
    return items.filter((it) =>
      colOrder.every((k) => {
        const f = (filters[k] || "").trim().toLowerCase();
        if (!f) return true;
        return String(cellValue(it, k)).toLowerCase().includes(f);
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, filters, departments, designations, colOrder]);

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const va = String(cellValue(a, sort.key)).toLowerCase();
      const vb = String(cellValue(b, sort.key)).toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort, departments, designations]);

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });

  const moveCol = (key, delta) => {
    setColOrder((order) => {
      const idx = order.indexOf(key);
      const newIdx = Math.max(0, Math.min(order.length - 1, idx + delta));
      if (idx === newIdx) return order;
      const out = [...order];
      out.splice(idx, 1); out.splice(newIdx, 0, key);
      return out;
    });
  };

  const visibleColList = colOrder.filter((k) => visibleCols[k]).map((k) => ALL_COLUMNS.find((c) => c.key === k));

  const renderCell = (it, k) => {
    switch (k) {
      case "employee_id": return <span className="font-mono text-xs">{it.employee_id}</span>;
      case "full_name": return <div><div className="font-medium">{it.full_name}</div></div>;
      case "mobile": return <span className="font-mono text-sm">{it.mobile}</span>;
      case "email": return <span className="text-sm">{it.email || "—"}</span>;
      case "role": return <Badge variant="outline" className="capitalize">{roleLabel(it.role)}</Badge>;
      case "department_id": return <span className="text-sm">{deptName(it.department_id) || "—"}</span>;
      case "designation_id": return <span className="text-sm">{desigName(it.designation_id) || "—"}</span>;
      case "status": return <StatusBadge status={it.status} />;
      case "created_at": return <span className="text-xs opacity-70">{fmtDate(it.created_at)}</span>;
      default: return null;
    }
  };

  return (
    <div data-testid="employees-page">
      <PageHeader
        title="Employees"
        description="Manage team members, roles, and access."
        action={
          canManage && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="employees-new-button" onClick={openCreate} className="btn-brand">
                  <Plus size={16} className="mr-1.5" /> New Employee
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editing ? "Edit Employee" : "Create Employee"}</DialogTitle>
                </DialogHeader>
                {activationLink && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs">
                    <div className="font-semibold text-amber-900 mb-1">Activation link (WhatsApp mocked)</div>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 truncate bg-white border border-amber-200 rounded px-2 py-1">{fullLink(activationLink)}</code>
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(fullLink(activationLink)); toast.success("Copied"); }}><Copy size={14} /></Button>
                    </div>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 flex items-center gap-4 p-3 rounded-md border" style={{ borderColor: "hsl(var(--border))" }}>
                    <div className="w-16 h-16 rounded-full border bg-white overflow-hidden flex items-center justify-center" style={{ borderColor: "hsl(var(--border))" }}>
                      {form.profile_picture_file_id ? (
                        <img src={fileUrl(form.profile_picture_file_id)} alt="" className="w-full h-full object-cover" />
                      ) : <UserCircle size={36} className="opacity-30" />}
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Profile Picture</Label>
                      <Input type="file" accept="image/*" data-testid="employee-pic-input" onChange={(e) => uploadPic(e.target.files?.[0])} className="mt-1" />
                      {picUploading && <p className="text-xs opacity-60 mt-1">Uploading…</p>}
                    </div>
                  </div>
                  <Field label="Staff Code">
                    <Input data-testid="employee-staff-code-input" placeholder="e.g. SMIPL-EMP-1010 (leave blank to auto-generate)" value={form.employee_id || ""} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} disabled={!!editing} />
                  </Field>
                  <Field label="Full Name *">
                    <Input data-testid="employee-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                  </Field>
                  <Field label="Mobile *">
                    <Input data-testid="employee-mobile-input" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} disabled={!!editing} />
                  </Field>
                  <Field label="Email">
                    <Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  </Field>
                  <Field label="Role">
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="employee-role-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </Field>
                  <Field label="Department">
                    <Select value={form.department_id || "none"} onValueChange={(v) => setForm({ ...form, department_id: v === "none" ? null : v })}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Designation">
                    <Select value={form.designation_id || "none"} onValueChange={(v) => setForm({ ...form, designation_id: v === "none" ? null : v })}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {designations.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Reporting Manager">
                    <Select value={form.reporting_manager_id || "none"} onValueChange={(v) => setForm({ ...form, reporting_manager_id: v === "none" ? null : v })}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {items.filter((e) => e.id !== editing?.id).map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  {editing && (
                    <Field label="Status">
                      <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["active", "inactive", "suspended", "pending_activation"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  <div className="sm:col-span-2 flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={!!form.is_department_manager} onChange={(e) => setForm({ ...form, is_department_manager: e.target.checked })} id="dm" />
                    <label htmlFor="dm">Mark as Department Manager (can approve travel/expense)</label>
                  </div>

                  {!editing && (
                    <>
                      <div className="sm:col-span-2 flex items-center gap-2 text-sm pt-2 border-t" style={{ borderColor: "hsl(var(--border))" }}>
                        <input
                          type="checkbox"
                          checked={!!form.set_password_directly}
                          onChange={(e) => setForm({ ...form, set_password_directly: e.target.checked, password: "" })}
                          id="setpwd"
                          data-testid="employee-setpwd-toggle"
                        />
                        <label htmlFor="setpwd">Set password directly (skip WhatsApp activation link)</label>
                      </div>
                      {form.set_password_directly && (
                        <div className="sm:col-span-2">
                          <Field label="Initial Password *">
                            <PasswordInput
                              data-testid="employee-password-input"
                              value={form.password}
                              onChange={(e) => setForm({ ...form, password: e.target.value })}
                              placeholder="At least 6 characters"
                            />
                          </Field>
                          <p className="text-xs mt-1 opacity-60">The employee can change this anytime from Settings → Change Password.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button data-testid="employee-save-button" onClick={save} className="btn-brand">{editing ? "Save changes" : "Create"}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="cols-toggle"><Columns size={14} className="mr-1" /> Columns</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Show / reorder columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {colOrder.map((k) => {
              const c = ALL_COLUMNS.find((c) => c.key === k);
              return (
                <div key={k} className="flex items-center justify-between px-2 py-1.5 text-sm gap-2" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenuCheckboxItem className="flex-1" checked={!!visibleCols[k]} onCheckedChange={(v) => setVisibleCols({ ...visibleCols, [k]: !!v })} onSelect={(e) => e.preventDefault()}>
                    {c.label}
                  </DropdownMenuCheckboxItem>
                  <button className="opacity-60 hover:opacity-100" onClick={(e) => { e.stopPropagation(); moveCol(k, -1); }} title="Move up"><CaretUp size={12} /></button>
                  <button className="opacity-60 hover:opacity-100" onClick={(e) => { e.stopPropagation(); moveCol(k, 1); }} title="Move down"><CaretDown size={12} /></button>
                </div>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button variant="outline" size="sm" onClick={() => setFilters({})} data-testid="clear-filters">Clear filters</Button>
        <div className="text-xs opacity-60 ml-2">{sorted.length} of {items.length} shown</div>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {visibleColList.map((c) => (
                  <TableHead key={c.key} className="whitespace-nowrap">
                    <button className="flex items-center gap-1 font-semibold" onClick={() => toggleSort(c.key)} data-testid={`sort-${c.key}`}>
                      {c.label}
                      {sort.key === c.key ? (sort.dir === "asc" ? <CaretUp size={12} /> : <CaretDown size={12} />) : <ArrowsDownUp size={12} className="opacity-40" />}
                    </button>
                  </TableHead>
                ))}
                {canManage && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
              <TableRow className="bg-slate-50/60 dark:bg-slate-900/40">
                {visibleColList.map((c) => (
                  <TableHead key={c.key} className="py-1.5">
                    <Input
                      placeholder={`Filter ${c.label}…`}
                      value={filters[c.key] || ""}
                      onChange={(e) => setFilters({ ...filters, [c.key]: e.target.value })}
                      className="h-7 text-xs"
                      data-testid={`filter-${c.key}`}
                    />
                  </TableHead>
                ))}
                {canManage && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 && (
                <TableRow><TableCell colSpan={visibleColList.length + (canManage ? 1 : 0)} className="text-center text-sm text-slate-500 py-10">No employees match the filters.</TableCell></TableRow>
              )}
              {sorted.map((e) => (
                <TableRow key={e.id} data-testid={`employee-row-${e.id}`}>
                  {visibleColList.map((c) => (
                    <TableCell key={c.key}>{renderCell(e, c.key)}</TableCell>
                  ))}
                  {canManage && (
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`employee-edit-${e.id}`}>Edit</Button>
                      <Button size="sm" variant="outline" onClick={() => resetPwd(e)}><KeyReturn size={14} className="mr-1" />Reset</Button>
                      <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(e)}>Disable</Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

export const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export const StatusBadge = ({ status }) => {
  const m = {
    active: "bg-green-100 text-green-800",
    inactive: "bg-slate-100 text-slate-700",
    suspended: "bg-red-100 text-red-800",
    pending_activation: "bg-amber-100 text-amber-800",
  };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m[status] || "bg-slate-100"}`}>{status.replace(/_/g, " ")}</span>;
};
